/**
 * Admin Prompt Detail Page
 * Editor, test playground, version history, and compare for a single prompt.
 */

import { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { toast } from 'sonner';
import apiClient from '../../lib/api';
import PromptEditor from '../../components/admin/PromptEditor';
import PromptVersionHistory from '../../components/admin/PromptVersionHistory';
import PromptTestPlayground from '../../components/admin/PromptTestPlayground';
import PromptCompareRunner from '../../components/admin/PromptCompareRunner';
import PromptSettings from '../../components/admin/PromptSettings';

interface PromptDetail {
  key: string;
  module: string;
  name: string;
  description: string | null;
  isOverridden: boolean;
  settings: Record<string, unknown>;
  codeDefault: {
    content: string;
    variables: string[];
    metadata: Record<string, unknown>;
    variableDefaults?: Record<string, string>;
  };
  currentOverride: {
    id: string;
    version: number;
    content: string;
    variables: string[];
    changeNote: string | null;
    createdAt: string;
  } | null;
}

interface VersionItem {
  id: string;
  version: number;
  content: string;
  changeNote: string | null;
  createdAt: string;
  isCurrent: boolean;
}

type Tab = 'editor' | 'test' | 'history' | 'compare' | 'settings';

export default function AdminPromptDetailPage() {
  const { key } = useParams<{ key: string }>();
  const decodedKey = decodeURIComponent(key || '');

  const [detail, setDetail] = useState<PromptDetail | null>(null);
  const [versions, setVersions] = useState<VersionItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<Tab>('editor');

  // Editor state
  const [editContent, setEditContent] = useState('');
  const [changeNote, setChangeNote] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const [detailRes, versionsRes] = await Promise.all([
        apiClient.get(`/admin/prompts/${encodeURIComponent(decodedKey)}`),
        apiClient.get(`/admin/prompts/${encodeURIComponent(decodedKey)}/versions`),
      ]);
      setDetail(detailRes.data);
      setVersions(versionsRes.data.versions || []);

      // Set editor content to current override or code default
      const d = detailRes.data as PromptDetail;
      setEditContent(
        d.isOverridden && d.currentOverride
          ? d.currentOverride.content
          : d.codeDefault.content
      );
    } catch (err) {
      console.error('Failed to fetch prompt detail:', err);
      toast.error('Prompt niet gevonden');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (decodedKey) fetchData();
  }, [decodedKey]);

  const handleSaveOverride = async () => {
    if (!detail) return;
    setIsSaving(true);
    try {
      await apiClient.post(
        `/admin/prompts/${encodeURIComponent(decodedKey)}/override`,
        {
          content: editContent,
          changeNote: changeNote || undefined,
        }
      );
      toast.success('Override opgeslagen');
      setChangeNote('');
      await fetchData();
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Opslaan mislukt');
    } finally {
      setIsSaving(false);
    }
  };

  const handleRemoveOverride = async () => {
    try {
      await apiClient.delete(
        `/admin/prompts/${encodeURIComponent(decodedKey)}/override`
      );
      toast.success('Override verwijderd, terug naar code default');
      await fetchData();
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Verwijderen mislukt');
    }
  };

  const handleRollback = async (version: number) => {
    try {
      await apiClient.post(
        `/admin/prompts/${encodeURIComponent(decodedKey)}/rollback`,
        { version }
      );
      toast.success(`Rollback naar versie ${version}`);
      await fetchData();
    } catch (err: any) {
      toast.error(err.response?.data?.error || 'Rollback mislukt');
    }
  };

  const handleSelectVersion = (v: VersionItem) => {
    setEditContent(v.content);
    setActiveTab('editor');
  };

  const currentVariables = useMemo(() => {
    if (!detail) return [];
    return detail.isOverridden && detail.currentOverride
      ? detail.currentOverride.variables
      : detail.codeDefault.variables;
  }, [detail]);

  const versionOptions = useMemo(() => {
    const opts = [{ id: 'default', label: 'Code Default' }];
    for (const v of versions) {
      opts.push({ id: v.id, label: `v${v.version}${v.isCurrent ? ' (actief)' : ''}` });
    }
    return opts;
  }, [versions]);

  // Check if editor content differs from active
  const hasChanges = useMemo(() => {
    if (!detail) return false;
    const activeContent =
      detail.isOverridden && detail.currentOverride
        ? detail.currentOverride.content
        : detail.codeDefault.content;
    return editContent !== activeContent;
  }, [detail, editContent]);

  if (isLoading) {
    return (
      <div className="flex justify-center py-16">
        <div className="w-8 h-8 border-2 border-gray-300 border-t-black dark:border-t-white rounded-full animate-spin" />
      </div>
    );
  }

  if (!detail) {
    return (
      <div className="text-center py-16 text-gray-500 dark:text-gray-400">
        Prompt niet gevonden.
      </div>
    );
  }

  const tabs: { id: Tab; label: string }[] = [
    { id: 'editor', label: 'Editor' },
    { id: 'test', label: 'Test' },
    { id: 'history', label: `Historie (${versions.length})` },
    { id: 'compare', label: 'Vergelijk' },
    { id: 'settings', label: 'Instellingen' },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <Link
          to="/admin/prompts"
          className="text-sm text-gray-500 dark:text-gray-400 hover:text-black dark:hover:text-white"
        >
          &larr; Alle prompts
        </Link>
        <h2 className="text-xl font-bold text-black dark:text-white mt-2">
          {detail.name}
        </h2>
        <div className="flex items-center gap-3 mt-1">
          <span className="font-mono text-xs text-gray-500 dark:text-gray-400">
            {detail.key}
          </span>
          <span className="px-2 py-0.5 text-xs font-mono border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400">
            {detail.module}
          </span>
          {detail.isOverridden ? (
            <span className="px-2 py-0.5 text-xs font-bold border-2 border-green-600 text-green-700 dark:text-green-400 bg-green-50 dark:bg-green-900/20">
              Override actief (v{detail.currentOverride?.version})
            </span>
          ) : (
            <span className="px-2 py-0.5 text-xs border border-gray-300 dark:border-gray-600 text-gray-500 dark:text-gray-400">
              Code default
            </span>
          )}
        </div>
        {detail.description && (
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
            {detail.description}
          </p>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b-2 border-black dark:border-gray-700">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2 text-sm font-bold border-2 border-b-0 transition-colors ${
              activeTab === tab.id
                ? 'border-black dark:border-gray-700 bg-white dark:bg-gray-900 text-black dark:text-white -mb-[2px]'
                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-black dark:hover:text-white'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {activeTab === 'editor' && (
        <div className="space-y-4">
          {/* Show code default for reference when overriding */}
          {detail.isOverridden && (
            <details className="border-2 border-gray-300 dark:border-gray-600">
              <summary className="px-3 py-2 text-xs font-bold text-gray-500 dark:text-gray-400 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800">
                Code default bekijken
              </summary>
              <pre className="p-3 text-xs font-mono text-gray-600 dark:text-gray-400 whitespace-pre-wrap bg-gray-50 dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 max-h-48 overflow-auto">
                {detail.codeDefault.content}
              </pre>
            </details>
          )}

          <PromptEditor
            content={editContent}
            onChange={setEditContent}
            variables={currentVariables}
          />

          {/* Save controls */}
          <div className="flex items-center gap-3">
            <input
              type="text"
              value={changeNote}
              onChange={(e) => setChangeNote(e.target.value)}
              placeholder="Notitie bij wijziging (optioneel)"
              className="flex-1 px-3 py-2 text-sm border-2 border-black dark:border-gray-700 bg-white dark:bg-gray-900 text-black dark:text-white"
            />
            <button
              onClick={handleSaveOverride}
              disabled={isSaving || !hasChanges}
              className="px-4 py-2 text-sm font-bold border-2 border-black dark:border-white bg-black dark:bg-white text-white dark:text-black shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] dark:shadow-[4px_4px_0px_0px_rgba(255,255,255,1)] hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] transition-all disabled:opacity-50"
            >
              {isSaving ? 'Opslaan...' : 'Override opslaan'}
            </button>
            {detail.isOverridden && (
              <button
                onClick={handleRemoveOverride}
                className="px-4 py-2 text-sm font-bold border-2 border-red-600 text-red-600 hover:bg-red-600 hover:text-white transition-colors"
              >
                Override verwijderen
              </button>
            )}
          </div>
        </div>
      )}

      {activeTab === 'test' && (
        <PromptTestPlayground
          promptKey={decodedKey}
          variables={currentVariables}
          variableDefaults={detail?.codeDefault.variableDefaults}
          content={hasChanges ? editContent : undefined}
        />
      )}

      {activeTab === 'history' && (
        <PromptVersionHistory
          versions={versions}
          onRollback={handleRollback}
          onSelect={handleSelectVersion}
          isLoading={false}
        />
      )}

      {activeTab === 'compare' && (
        <PromptCompareRunner
          promptKey={decodedKey}
          variables={currentVariables}
          versionOptions={versionOptions}
        />
      )}

      {activeTab === 'settings' && (
        <PromptSettings
          promptKey={decodedKey}
          currentMetadata={(detail.settings || detail.codeDefault.metadata || {}) as Record<string, unknown>}
          onSaved={fetchData}
        />
      )}
    </div>
  );
}
